import type { ApolloClient } from '@apollo/client/core';

import type { ProjectLanguageFragment } from '~/graphql/types';
import { ProjectLanguagesQueryDocument } from '~/graphql/types';

// GitLab rejects a projects query with more than 50 full paths.
const MAX_PATHS_PER_QUERY = 50;

const splitIntoBatches = (paths: string[]) => {
  const batches: string[][] = [];

  for (let start = 0; start < paths.length; start += MAX_PATHS_PER_QUERY) {
    batches.push(paths.slice(start, start + MAX_PATHS_PER_QUERY));
  }

  return batches;
};

const fetchProjects = async (client: Pick<ApolloClient, 'query'>, projectPaths: string[]) => {
  const requests = splitIntoBatches(projectPaths).map(fullPaths => client.query({
    query: ProjectLanguagesQueryDocument,
    variables: { fullPaths, first: MAX_PATHS_PER_QUERY },
  }));
  const responses = await Promise.all(requests);

  return responses.flatMap(response => response.data.projects?.nodes ?? []);
};

const shareOf = (language: ProjectLanguageFragment) => language.share ?? 0;

const primaryLanguageOf = (languages: ProjectLanguageFragment[]) => {
  let primary = languages[0];

  for (const language of languages) {
    if (shareOf(language) > shareOf(primary)) {
      primary = language;
    }
  }

  return primary;
};

export const fetchPrimaryLanguages = async (
  client: Pick<ApolloClient, 'query'>,
  projectPaths: string[],
): Promise<Map<string, string>> => {
  const projects = await fetchProjects(client, projectPaths);
  const primaryLanguages = new Map<string, string>();

  for (const project of projects) {
    if (project?.languages?.length) {
      primaryLanguages.set(project.fullPath, primaryLanguageOf(project.languages).name);
    }
  }

  return primaryLanguages;
};
